"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { SectionLayout } from "@/components/layout/SectionLayout";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { formula } from "@/data/formulas";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";

export const FormulasSection = () => {
  const t = useTranslations("formulas");

  const translatedFormula = {
    title: t(formula.titleKey),
    subtitle: t(formula.subtitleKey),
    features: t.raw(formula.featuresKey) as string[],
    price: t(formula.priceKey),
    addons: formula.addonsKey ? (t.raw(formula.addonsKey) as string[]) : [],
  };

  // État des toggles (off par défaut)
  const [selectedAddons, setSelectedAddons] = useState<boolean[]>(
    Array.from({ length: translatedFormula.addons.length }, () => false),
  );

  const toggleIndex = (idx: number) =>
    setSelectedAddons((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.05 },
    },
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -12 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  };

  // Composant Toggle accessible + animation fluide
  const Toggle = ({ checked }: { checked: boolean }) => (
    <span
      className={`relative inline-flex h-[18px] w-[34px] flex-shrink-0 items-center rounded-full ring-1 ring-black/5 transition-colors duration-300 ease-in-out ${
        checked ? "bg-accent" : "bg-[#D9D9D9]/80"
      }`}
      aria-hidden
    >
      <span
        className={`absolute top-1/2 h-[14px] w-[14px] -translate-y-1/2 transform rounded-full bg-white shadow transition-transform duration-300 ease-in-out ${
          checked ? "translate-x-[18px]" : "translate-x-[2px]"
        }`}
      />
    </span>
  );

  return (
    <SectionLayout id="offre">
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, margin: "-60px" }}
        className="w-full rounded-xl border border-[#EDEDED] bg-primary p-2 shadow-[0_8px_24px_0_rgba(18,18,18,0.05)]"
      >
        <div className="flex flex-col gap-4 rounded-xl bg-primary lg:flex-row">
          {/* Colonne gauche : devis + CTA */}
          <div className="order-1 flex w-full items-center justify-center p-8 lg:order-1 lg:w-1/2 lg:p-12">
            <div className="flex w-full max-w-sm flex-col items-center text-center">
              <div className="text-secondary">{translatedFormula.title}</div>
              <div className="mt-2 text-[40px] font-bold leading-tight text-secondary sm:text-[44px]">
                {translatedFormula.price}
              </div>
              <div className="mt-2 text-secondary/80">
                {translatedFormula.subtitle}
              </div>
              <div className="mt-10">
                <PrimaryButton
                  text={t("common.appointment")}
                  className="px-6"
                  navigateTo="https://calendly.com/agence-virtuosa/appel-decouverte"
                  target="_blank"
                />
              </div>
            </div>
          </div>

          {/* Colonne droite : features + options */}
          <div className="order-2 w-full lg:order-2 lg:w-1/2">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
              viewport={{ once: true }}
              className="h-full rounded-md bg-tertiary p-6 sm:p-8"
            >
              {/* Features */}
              <motion.ul
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-4"
              >
                {translatedFormula.features.map((feature, i) => (
                  <motion.li
                    key={i}
                    variants={listItemVariants}
                    className="flex items-start gap-3"
                  >
                    <Icon
                      icon="mdi:check"
                      className="h-[22px] w-[16px] flex-shrink-0 text-accent"
                    />
                    <span className="text-sm text-secondary">{feature}</span>
                  </motion.li>
                ))}
              </motion.ul>

              {/* Séparateur aligné */}
              <div className="my-4 h-px w-[calc(100%-28px)] bg-[#E5E5E5]" />

              {/* Options avec toggles cliquables */}
              {translatedFormula.addons.length > 0 && (
                <motion.ul
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="space-y-[12px]"
                >
                  {translatedFormula.addons.map((addon, i) => (
                    <motion.li key={i} variants={listItemVariants}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={selectedAddons[i]}
                        onClick={() => toggleIndex(i)}
                        className="group flex w-full cursor-pointer select-none items-center gap-3 rounded-md p-1 text-left outline-none transition"
                      >
                        <Toggle checked={selectedAddons[i]} />
                        <span className="text-sm text-secondary">{addon}</span>
                      </button>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </SectionLayout>
  );
};
